if (!customElements.get('complete-look-bundle')) {
  customElements.define('complete-look-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.cards = [...this.querySelectorAll('[data-clb-product]')];
      this.style.setProperty('--clb-products', this.cards.length);
      this.currency = this.dataset.currency || 'USD';

      this.track = this.querySelector('[data-clb-track]');
      this.prevButton = this.querySelector('[data-clb-prev]');
      this.nextButton = this.querySelector('[data-clb-next]');
      this.handleCarouselScroll = () => this.updateCarouselButtons();
      this.prevButton?.addEventListener('click', () => this.scrollCarousel(-1));
      this.nextButton?.addEventListener('click', () => this.scrollCarousel(1));
      this.track?.addEventListener('scroll', this.handleCarouselScroll, { passive: true });
      window.addEventListener('resize', this.handleCarouselScroll, { passive: true });
      this.updateCarouselButtons();
      window.requestAnimationFrame(() => this.updateCarouselButtons());

      this.cards.forEach((card) => {
        card.querySelector('[data-clb-check]')?.addEventListener('change', () => this.update());
        card.querySelector('[data-clb-variant]')?.addEventListener('change', (event) => {
          const option = event.target.selectedOptions[0];
          card.dataset.variantId = option.value;
          card.dataset.price = option.dataset.price;
          const image = card.querySelector('[data-clb-image]');
          if (image && option.dataset.image) image.src = option.dataset.image;
          const price = card.querySelector('[data-clb-price]');
          if (price) price.textContent = this.money(Number(option.dataset.price));
          this.update();
        });
      });

      this.currentCard = this.cards.find((card) => card.dataset.current === 'true');
      this.currentVariantId = this.currentCard?.dataset.variantId;
      this.syncCurrentVariant();
      this.variantTimer = window.setInterval(() => this.syncCurrentVariant(), 400);
      this.querySelector('[data-clb-add]')?.addEventListener('click', () => this.add());
      this.update();
    }

    disconnectedCallback() {
      window.clearInterval(this.variantTimer);
      this.track?.removeEventListener('scroll', this.handleCarouselScroll);
      window.removeEventListener('resize', this.handleCarouselScroll);
    }

    scrollCarousel(direction) {
      if (!this.track) return;
      const firstCard = this.cards[0];
      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const step = firstCard ? firstCard.getBoundingClientRect().width + gap : this.track.clientWidth;
      this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    updateCarouselButtons() {
      if (!this.track) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const current = Math.abs(this.track.scrollLeft);
      if (this.prevButton) this.prevButton.disabled = current <= 2;
      if (this.nextButton) this.nextButton.disabled = current >= maxScroll - 2 || maxScroll <= 2;
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.currency }).format(cents / 100);
      } catch (_) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    async syncCurrentVariant() {
      if (!this.currentCard || this.syncingVariant) return;
      const productInfo = this.closest('product-info') || document;
      const field = productInfo.querySelector('form[action*="/cart/add"] input[name="id"]');
      const variantId = field?.value;
      if (!variantId || variantId === this.currentVariantId) return;

      this.currentVariantId = variantId;
      this.currentCard.dataset.variantId = variantId;
      this.syncingVariant = true;
      try {
        const response = await fetch(`${window.location.pathname}.js`);
        const productData = await response.json();
        const variant = productData.variants.find((item) => String(item.id) === String(variantId));
        if (!variant) return;
        this.currentCard.dataset.price = variant.price;
        const price = this.currentCard.querySelector('[data-clb-price]');
        if (price) price.textContent = this.money(variant.price);
        const image = this.currentCard.querySelector('[data-clb-image]');
        if (image && variant.featured_image?.src) image.src = variant.featured_image.src;
        this.update();
      } catch (_) {
        /* Keep the main product form usable if its JSON cannot be refreshed. */
      } finally {
        this.syncingVariant = false;
      }
    }

    update() {
      const selected = this.cards.filter((card) => card.querySelector('[data-clb-check]')?.checked);
      this.cards.forEach((card) => card.classList.toggle('is-selected', card.querySelector('[data-clb-check]')?.checked));
      const count = selected.length;
      const countNode = this.querySelector('[data-clb-count]');
      if (countNode) countNode.textContent = `${count}/${this.cards.length}`;

      let tier = 0;
      if (count >= 4) tier = 4;
      else if (count === 3) tier = 3;
      else if (count === 2) tier = 2;

      const rawPercentage = tier ? Number(this.getAttribute(`data-tier-${tier}-percent`) || 0) : 0;
      const rawFixedAmount = tier ? Number(this.getAttribute(`data-tier-${tier}-fixed`) || 0) : 0;
      const percentageDiscount = Number.isFinite(rawPercentage) ? Math.min(100, Math.max(0, rawPercentage)) : 0;
      const fixedDiscountCents = Number.isFinite(rawFixedAmount) ? Math.max(0, Math.round(rawFixedAmount * 100)) : 0;

      this.querySelectorAll('[data-clb-tier]').forEach((tier) => {
        tier.classList.toggle('is-active', count >= Number(tier.dataset.clbTier));
      });

      const original = selected.reduce((sum, card) => sum + Number(card.dataset.price || 0), 0);
      const percentageAdjustedTotal = Math.round(original * (100 - percentageDiscount) / 100);
      const total = Math.max(0, percentageAdjustedTotal - fixedDiscountCents);
      const savings = Math.max(0, original - total);
      const totalNode = this.querySelector('[data-clb-total]');
      const originalNode = this.querySelector('[data-clb-original]');
      const savingNode = this.querySelector('[data-clb-saving]');
      if (totalNode) totalNode.textContent = this.money(total);
      if (originalNode) {
        originalNode.textContent = savings > 0 ? this.money(original) : '';
        originalNode.hidden = savings <= 0;
      }
      if (savingNode) {
        savingNode.textContent = savings > 0 ? `-${this.money(savings)}` : '';
        savingNode.hidden = savings <= 0;
      }
      const note = this.querySelector('[data-clb-note]');
      if (note) note.hidden = savings <= 0;
      const button = this.querySelector('[data-clb-add]');
      if (button) button.disabled = count === 0;
    }

    updateCartBubble(headerHtml) {
      if (!headerHtml) return;
      const parsedHeader = new DOMParser().parseFromString(headerHtml, 'text/html');
      const newBubble = parsedHeader.querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');
      if (newBubble && currentBubble) currentBubble.replaceWith(newBubble);
    }

    updateCartDrawer(drawerHtml) {
      if (!drawerHtml) return false;

      const parsedDrawer = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const newSection = parsedDrawer.querySelector('#shopify-section-cart-drawer');
      const newDrawer = parsedDrawer.querySelector('cart-drawer');
      const currentSection = document.querySelector('#shopify-section-cart-drawer');
      const currentDrawer = document.querySelector('cart-drawer');

      if (currentSection && newSection) currentSection.innerHTML = newSection.innerHTML;
      else if (currentSection && newDrawer) currentSection.innerHTML = newDrawer.outerHTML;
      else if (currentSection) currentSection.innerHTML = drawerHtml;
      else if (currentDrawer && newDrawer) currentDrawer.outerHTML = newDrawer.outerHTML;
      else return false;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return false;
      drawer.classList.remove('is-empty');
      if (typeof drawer.open === 'function') drawer.open();
      else {
        drawer.classList.add('active');
        document.body.classList.add('overflow-hidden');
      }
      return true;
    }

    async add() {
      const button = this.querySelector('[data-clb-add]');
      const error = this.querySelector('[data-clb-error]');
      const items = this.cards
        .filter((card) => card.querySelector('[data-clb-check]')?.checked)
        .map((card) => ({ id: Number(card.dataset.variantId), quantity: 1, properties: { '_Bundle': 'Complete the look' } }));
      if (!items.length) return;

      button.disabled = true;
      button.classList.add('is-loading');
      error.hidden = true;
      try {
        const sections = ['cart-drawer', 'header'];
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: location.pathname })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add bundle to cart.');
        const drawerUpdated = this.updateCartDrawer(data.sections?.['cart-drawer']);
        this.updateCartBubble(data.sections?.header);
        if (!drawerUpdated) window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (exception) {
        error.textContent = exception.message;
        error.hidden = false;
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  });
}
